/**
 * Thin wrappers around the orval-generated trace hooks.
 *
 * Unwraps the orval `{data, status, headers}` shape to a flat
 * `{ data, isLoading, error }` so pages don't repeat the discriminated-union
 * dance. Traces are read-only, so no mutations here.
 */

import type { ApiRequestError } from '@/api/client'
import {
  useGetTraceApiTracesTraceKeyGet,
  useListConversationsApiTracesConversationsGet,
} from '@/api/generated/traces/traces'
import type {
  ConversationListResponse,
  ListConversationsApiTracesConversationsGetParams,
  TraceDocument,
} from '@/api/generated/model'

export type { ConversationListResponse }

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

/** Allowed `range` literals, from the generated client. `all` is
    conversations only: it's one D1 query, where a requests listing walks
    R2 day prefixes. */
export type ConversationRange = NonNullable<ListConversationsApiTracesConversationsGetParams['range']>

interface UseConversationsArgs {
  params: ListConversationsApiTracesConversationsGetParams
}

/**
 * Browse the team's conversations: a relative window, a search, the
 * client metadata filters, a sort and a page (TRA-1397), as the address
 * bar carries them.
 *
 * Backed by the gateway's D1 conversation index (audit-log
 * `conversation_id` column). `params.model` narrows to conversations that
 * called one platform model, resolved to the canonical team-prefixed slug
 * server-side; `params.model_slug` is that slug directly.
 */
export function useConversations({
  params,
}: UseConversationsArgs): UnwrappedQuery<ConversationListResponse> {
  const query = useListConversationsApiTracesConversationsGet(params)
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * One saved trace by its full R2 key (TRA-1412): the call's request and
 * response bodies as the gateway kept them. Disabled while no key is
 * chosen, so a closed detail sheet fetches nothing.
 */
export function useTrace(traceKey: string | null): UnwrappedQuery<TraceDocument> {
  const query = useGetTraceApiTracesTraceKeyGet(traceKey ?? '', {
    query: { enabled: traceKey !== null },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}
